// LogsList.tsx
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  InputRef,
  Popover,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import { TFilterModel, TResponseModel } from "../../../types/config";
import {
  APPROACH,
  ePrivileges,
  eREPORTTYPE,
  eResultCode,
  REPORTDURATION,
  reportTypeMapping,
} from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { dateFormat, defaultFilterParams } from "../../../utils/constants";
import {
  AddEditReportData,
  GetAMCSummaryReport,
  GetReportDataList,
  GetReportLogsList,
} from "../../../utils/api.constant";
import utils from "../../../utils";
import { TReport } from "../../../types";
import DateUtils from "../../../utils/date";
import ColumnManager from "../../../components/configurable-columns";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  FilterFilled,
  FilterOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import Highlighter from "react-highlight-words";
import { FilterDropdownProps } from "antd/es/table/interface";
import Utils from "../../../utils";
import CustomDrawer from "../../../components/drawer";
import ReportForm from "./form";
import { UserContext } from "../../../context";
import SummaryReportForm from "./summary-form";
import { render } from "@testing-library/react";
import GenericPopover from "../../../components/pop-over";
import ActionCard from "../../../components/actionCard/actionCard";
import CustomModal from "../../../components/modal";

export default function SummaryList() {
  // initialization of hooks
  const { post } = useFetch(); // custom hook used to call the apis
  const [isLoading, setIsLoading] = useState(true); // to manage the loader
  const [data, setData] = useState<TReport[]>([]); // to store config group data
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams); // object to fetch the data in list
  const { onShowToast } = useToast(); // custom hook used to show toast
  const searchInput = useRef<InputRef>(null);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const context: any = useContext(UserContext);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState<TReport | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const handleReset = (clearFilters: FilterDropdownProps["clearFilters"]) => {
    if (clearFilters) {
      clearFilters();
    }
    setSearchText("");
  };

  const showStatusUpdateModal = (report: TReport) => {
    setSelectedReport(report);
    setIsStatusModalVisible(true);
  };

  const getColumnSearchProps = (dataIndex: any): any => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e: any) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,.45)", marginRight: 4 }}
            />
          } // Adds and spaces the search icon
          value={selectedKeys[0]}
          onChange={(e: any) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px", // Ensure spacing for both icon and text
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: any
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const initialColumns: any[] = [
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      width: "10%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.customerName).localeCompare(String(b.customerName)),
      filters: utils.getUniqueFilters(data, "customerName"),
      onFilter: (value: string | number | boolean, record: TReport) =>
        record.customerName.includes(value as string),
    },
    {
      title: "Report Duration",
      dataIndex: "reportDuration",
      key: "reportDuration",
      width: "7%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.reportDuration).localeCompare(String(b.reportDuration)),
      filters: [
        { text: "Month", value: REPORTDURATION.MONTH },
        { text: "Quarter", value: REPORTDURATION.QUARTER },
        { text: "Year", value: REPORTDURATION.YEAR },
      ],
      onFilter: (value: string | number | boolean, record: TReport) =>
        record.reportDuration === value,
      render: (duration: number) =>
        duration === REPORTDURATION.MONTH
          ? "Month"
          : duration === REPORTDURATION.QUARTER
            ? "Quarter"
            : duration === REPORTDURATION.YEAR
              ? "Year"
              : "-",
    },
    {
      title: "Tax Heading",
      dataIndex: "taxHeading",
      key: "taxHeading",
      width: "10%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.taxHeading).localeCompare(String(b.taxHeading)),
      // filters: utils.getUniqueFilters(data, "taxHeading"),
      // onFilter: (value: string | number | boolean, record: TReport) =>
      //   record?.taxHeading?.includes(value as string),
    },
    {
      title: "Tax Percent",
      dataIndex: "taxPercent",
      key: "taxPercent",
      width: "5%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.taxPercent).localeCompare(String(b.taxPercent)),
    },
    {
      title: "Cal. Approach",
      dataIndex: "calculationApproach",
      key: "calculationApproach",
      width: "10%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.calculationApproach).localeCompare(
          String(b.calculationApproach)
        ),
      filters: [
        { text: "Approach 1", value: APPROACH.APPROACH1 },
        { text: "Approach 2", value: APPROACH.APPROACH2 },
      ],
      onFilter: (value: string | number | boolean, record: TReport) =>
        record.calculationApproach === value,
      render: (approach: number, record: TReport) => (
        <span>
          {approach === APPROACH.APPROACH1 ? (
            <span> Approach 1</span>
          ) : approach === APPROACH.APPROACH2 ? (
            <Popover
              content={
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {record?.approachTwoParams?.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        cursor: "pointer",
                        border: "1px solid #ccc",
                        padding: "10px",
                        borderRadius: "1px",
                        width: "150px",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>Gyro:</span>{" "}
                      <span>{item.gyro === 1 ? "Yes" : "No"}</span>
                      <span style={{ fontWeight: "bold" }}>UPS:</span>{" "}
                      <span>{item.ups === 1 ? "Yes" : "No"}</span>
                      <br />
                      <span style={{ fontWeight: "bold" }}>
                        Percentage:
                      </span>{" "}
                      <span>{item.percentage}%</span>
                    </div>
                  ))}
                </div>
              }
              title="Title"
            >
              <span
                style={{
                  cursor: "pointer",
                  color: "var(--bs-primary) !important",
                }}
              >
                {" "}
                Approach 2
              </span>
            </Popover>
          ) : (
            "-"
          )}
        </span>
      ),
    },
    {
      title: "Downloaded On",
      dataIndex: "createdOn",
      key: "createdOn",
      width: "15%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.createdOn).localeCompare(String(b.createdOn)),
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, dateFormat);
      },
    },
    {
      title: "Status",
      dataIndex: "isApproved",
      key: "isApproved",
      width: "5%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.isApproved).localeCompare(String(b.isApproved)),
      filters: [
        { text: "Pending", value: 0 },
        { text: "Approved", value: 1 },
      ],
      onFilter: (value: string | number | boolean, record: TReport) =>
        record?.isApproved === value,
      render: (status: number) => (status === 1 ? "Approved" : "Pending"),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "4%",
      render: (record: TReport) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {!record.isApproved &&
                Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.APPROVEAMCSUMMARYREPORT
                ) ? (
                  <ActionCard
                    // icon={faCalendarCheck}
                    text="Approve"
                    onClick={() => showStatusUpdateModal(record)}
                  >
                    <CheckCircleOutlined style={{ fontSize: 18 }} />
                  </ActionCard>
                ) : null}
                <a href={record?.filePath} download>
                  <div style={{ display: "flex" }}>
                    <div>
                      <DownloadOutlined style={{ fontSize: "20px" }} />
                    </div>
                    <div style={{ marginLeft: "20px" }}>
                      <span>Download</span>
                    </div>
                  </div>
                </a>
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];

  const handleStatusUpdateCancel = () => {
    setSelectedReport(null);
    setIsStatusModalVisible(false);
  };

  const handleStatusUpdateOk = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          id: selectedReport?.id,
          isApproved: 1,
        },
      };

      const response: TResponseModel = await post(
        AddEditReportData,
        requestPayload
      );

      const { dataResponse } = response;
      const { returnCode } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "Status updated successfully!",
        });
        setSelectedReport(null);
        setIsStatusModalVisible(false);
        await fetchData();
      }
    } catch (error) {
      console.log("ERROR WHILE UPDATING STATUS", error);
    } finally {
      setIsLoading(false);
    }
  };

  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns);

  useEffect(() => {
    fetchData();
  }, []);

  const updateColumnFilters = (dataset: TReport[]) => {
    const customerNameFilters = Array.from(
      new Set(dataset.map((item) => item.customerName))
    ).map((name) => ({ text: name, value: name }));

    const reportDurationFilters = [
      { text: "Month", value: REPORTDURATION.MONTH },
      { text: "Quarter", value: REPORTDURATION.QUARTER },
      { text: "Year", value: REPORTDURATION.YEAR },
    ].filter((option) =>
      dataset.some((item) => item.reportDuration === option.value)
    );

    const calculationApproachFilters = [
      { text: "Approach 1", value: APPROACH.APPROACH1 },
      { text: "Approach 2", value: APPROACH.APPROACH2 },
    ].filter((option) =>
      dataset.some((item) => item.calculationApproach === option.value)
    );

    const statusFilters = [
      { text: "Pending", value: 0 },
      { text: "Approved", value: 1 },
    ].filter((option) =>
      dataset.some((item) => item.isApproved === option.value)
    );

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "customerName") {
          return { ...col, filters: customerNameFilters };
        }
        if (col.key === "reportDuration") {
          return { ...col, filters: reportDurationFilters };
        }
        if (col.key === "calculationApproach") {
          return { ...col, filters: calculationApproachFilters };
        }
        if (col.key === "isApproved") {
          return { ...col, filters: statusFilters };
        }
        return col;
      })
    );
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(globalsearchText.toLowerCase())
    );

    const customerNameMatch =
      !activeFilters.customerName ||
      activeFilters.customerName.includes(item.customerName);

    const reportDurationMatch =
      !activeFilters.reportDuration ||
      activeFilters.reportDuration.includes(Number(item.reportDuration));

    const calculationApproachMatch =
      !activeFilters.calculationApproach ||
      activeFilters.calculationApproach.includes(
        Number(item.calculationApproach)
      );

    const statusMatch =
      !activeFilters.isApproved ||
      activeFilters.isApproved.includes(Number(item.isApproved));

    return (
      searchMatch &&
      customerNameMatch &&
      reportDurationMatch &&
      calculationApproachMatch &&
      statusMatch
    );
  });

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetReportDataList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TReport, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          ...item,
        }));
        setData(formattedData);
        setFilterParams({
          ...(filterModel as TFilterModel),
          totalRows: data.length,
        });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((report: TReport) => {
    const { isApproved, reportDuration, calculationApproach, ...restReport } =
      report;
    return {
      calculationApproach:
        report.calculationApproach === APPROACH.APPROACH1
          ? "Approach 1"
          : report.calculationApproach === APPROACH.APPROACH2
            ? "Approach 2"
            : "",
      reportDuration:
        report.reportDuration === REPORTDURATION.MONTH
          ? "Month"
          : report.reportDuration === REPORTDURATION.QUARTER
            ? "Quarter"
            : report.reportDuration === REPORTDURATION.YEAR
              ? "Year"
              : "-",
      isApproved: report.isApproved === 1 ? "Approved" : "Pending",
      ...restReport,
    };
  });

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        // icon={<FaObjectGroup />}
        title={`${filterParams?.totalRows} Total AMC Summary Reports`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.DOWNLOADAMCSUMMARYREPORT
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Generate
                </Button>
              }
              componentProps={{
                onRefreshList: () => fetchData(),
              }}
              component={SummaryReportForm} // Passing DealerForm component
              title="Reports"
              size="default"
            />
          ) : null}

          {/* MOVED TO REPORT LIST */}
          {/* <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Schedule
              </Button>
            }
            component={ScheduleReportForm} // Passing DealerForm component
            title="Schedule Report"
            size="default"
          /> */}
          <Input
            placeholder="Filter by keywords"
            value={globalsearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`amc_summary_reports_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>
      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>

      <CustomModal
        title="Confirm Status Update"
        isOpen={isStatusModalVisible}
        onOk={handleStatusUpdateOk}
        onCancel={() => handleStatusUpdateCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          You are about to approve report for{" "}
          <b>{selectedReport?.customerName}</b>?
        </p>
      </CustomModal>
    </div>
  );
}
